import { BulkApprovalBar } from "../BulkApprovalBar";
import { TruncatedTableValue } from "components/DesignSystem/tableColumnSizing";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { customGetAPI } from "services/utils/CustomAPI";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { StatusRow } from "components/CustomComponents/StatusRow";
import ApprovalAuditTrail from "components/DesignSystem/ApprovalAuditTrail";
import {
  scrollTop,
  getStatusFlow,
  StatusBadge,
  displayFlexItem,
} from "services/constants";
import { Apply } from "./Apply";
import { useSelector } from "react-redux";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { Loader } from "components/Loader";
import { Box } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { showToast } from "services/utils/Status";
import { TeamZoneCompOff } from "../Headers";
import { CompOffDetailPanel } from "./CompOffDetailPanel";
import { useSavedHeaderViews } from "components/CustomComponents/useSavedHeaderViews";
import { SavedViewSelector } from "components/CustomComponents/SavedViewSelector";
import {
  CompOffStatusCards,
  canonicalCompOffStatus,
  compOffStatusVisual,
} from "./CompOffStatusCards";
import { useExpandablePanel } from "services/hooks/useExpandablePanel";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  designType,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import {
  bulkSelectionCheckboxSx,
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableHeadCell,
  approveTableBodyCell,
  NewRequestButton,
  ApproveTable,
} from "../approveUi";
import moment from "moment";
import { resolveRangeLabel } from "../dateRangeUtils";
import { normalizeApprovalPersonFields } from "../displayNameUtils";

// Same "Default View" sentinel pattern as Leave/index.js.
const COMPOFF_LIST_DEFAULT_VIEW_ID = "__default_compoff_list_view__";

// Same date-format tolerance WorkAdjustment/index.js's own toISODate uses —
// the actual-special-attendance API's date fields aren't guaranteed to
// already be ISO, so this normalizes DD-MM-YYYY-ish strings before resend.
const toISODate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const parts = dateStr.split("-");
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return dateStr;
};

export const CompOff = ({
  setReCallApi,
  reCallApi,
  startDate,
  endDate,
  dateRangeLabel: dateRangeLabelProp,
  handleStartDate,
  subModuleId,
}) => {
  const { domain, token, userDetails, topbarCompanyId, topbarCompanyName, userScreenAccess } =
    useSelector((state) => state.tokenReducer);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const containerRef = useRef(null);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const footerRef = useCallback((node) => setFooterNode(node), []);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [compOffData, setCompOffData] = useState([]);
  const [compOffFilterData, setCompOffFilterData] = useState([]);
  const [viewCompOffDetails, setViewCompOffDetails] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [expandedData, setExpandedData] = useState(null);
  const [statusCardFilter, setStatusCardFilter] = useState("");
  // Initialized from (and kept in sync with) the Approval Center landing
  // page's own dateRangeLabel prop — previously hardcoded to "Last 7 Days"
  // regardless of the actual startDate/endDate this screen was handed, on
  // a fresh ?view= deep link or Team Workspace Home's "date" param. A
  // manual re-pick on this screen's own picker still overrides it locally.
  const [dateRangeLabel, setDateRangeLabel] = useState(
    dateRangeLabelProp || "Last 7 Days",
  );

  useEffect(() => {
    if (dateRangeLabelProp) setDateRangeLabel(dateRangeLabelProp);
  }, [dateRangeLabelProp]);
  const [selectedRowKeys, setSelectedRowKeys] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showBulkRejectDialog, setShowBulkRejectDialog] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState("");
  const {
    expanded: compOffPanelExpanded,
    toggleExpanded: toggleCompOffPanelExpanded,
  } = useExpandablePanel(viewCompOffDetails);
  const {
    expanded: createCompOffPanelExpanded,
    toggleExpanded: toggleCreateCompOffPanelExpanded,
  } = useExpandablePanel(isCreatePanelOpen);

  useEffect(() => {
    const fetchCompOffDetails = async () => {
      const params = new URLSearchParams({
        user_id: String(userDetails?.id ?? ""),
        company_id: String(topbarCompanyId ?? ""),
        start_date: String(startDate ?? ""),
        end_date: String(endDate ?? ""),
        attendance_type: "Comp off",
      });
      const queryString = params.toString().replace(/\+/g, "%20");
      const res = await customGetAPI(
        `${domain}team_zone/approve/work_adjustment/actual-special-attendance/?${queryString}&company_id=${topbarCompanyId}`,
      );
      const rows = Array.isArray(res?.responseDetails)
        ? res.responseDetails
        : (res?.responseDetails?.results ?? []);
      const mappedRows = rows
        .filter((row) => row?.attendance_type === "Comp off")
        .map((row) => ({
          ...row,
          // getStatusFlow (services/constants) already reads these same
          // fields for the expand row's approval timeline — one column
          // showing whichever of the three actually fired for this row.
          approver_name:
            row.approved_by || row.rejected_by || row.cancelled_by || "-",
        }))
        .map(normalizeApprovalPersonFields);
      setCompOffData(mappedRows);
      // Also seed the filtered view directly — CustomFilter recomputes it
      // in its own effect a render later, so without this the table
      // flashes "No Matching Results" for a frame right as loading ends.
      setCompOffFilterData(mappedRows);
      setLoading(false);
      setReCallApi(false);
    };
    if (startDate && endDate && reCallApi) {
      setLoading(true);
      fetchCompOffDetails();
    }
  }, [
    domain,
    endDate,
    reCallApi,
    setReCallApi,
    startDate,
    topbarCompanyId,
    userDetails?.id,
  ]);

  // Checks its own "Comp Off" submodule rather than sharing WorkAdjustment's
  // bucket — the backend doesn't provision this submodule yet, so this
  // reads as no access until it's added there.
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Approvals",
    "Comp Off",
  );

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    if (!footerNode) return;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  useEffect(() => {
    if (!customFilterNode) return;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setCompOffFilterData(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  // Tallies for the Pending/Approved/Rejected cards using current filters.
  const compOffStatusCounts = useMemo(
    () =>
      (compOffFilterData || []).reduce((acc, request) => {
        const status = canonicalCompOffStatus(request?.status);
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {}),
    [compOffFilterData],
  );

  const visibleCompOffData = useMemo(() => {
    if (!statusCardFilter) return compOffFilterData;
    return compOffFilterData.filter(
      (request) => canonicalCompOffStatus(request?.status) === statusCardFilter,
    );
  }, [compOffFilterData, statusCardFilter]);

  const handleStatusCardClick = (key) => {
    clearSelection();
    setShowBulkRejectDialog(false);
    setPage(0);
    setExpandedIndex(null);
    setExpandedData(null);
    setViewCompOffDetails(false);
    setSelectedData(null);
    if (key === "All") {
      setStatusCardFilter("");
    } else {
      setStatusCardFilter(key);
    }
  };

  // Bulk select/approve/reject — mirrors the multi-select action bar the
  // old combined WorkAdjustment screen has always had, which this split
  // otherwise missed carrying over. Selection is scoped to the rows
  // currently visible in the table (post status-card + CustomFilter
  // filtering) so "select all" always matches what's on screen.
  const isSelected = (row) => selectedRowKeys.has(row.id);

  const pendingRows = useMemo(
    () => visibleCompOffData.filter((row) => row.status === "Pending"),
    [visibleCompOffData],
  );

  const toggleRow = (row) => {
    if (row.status !== "Pending") return;
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      next.has(row.id) ? next.delete(row.id) : next.add(row.id);
      return next;
    });
  };

  const toggleAll = () => {
    const allPendingSelected =
      pendingRows.length > 0 && pendingRows.every((r) => isSelected(r));
    if (allPendingSelected) {
      setSelectedRowKeys(new Set());
    } else {
      setSelectedRowKeys(new Set(pendingRows.map((r) => r.id)));
    }
  };

  const isAllSelected =
    pendingRows.length > 0 && pendingRows.every((r) => isSelected(r));
  const isIndeterminate =
    selectedRowKeys.size > 0 && selectedRowKeys.size < pendingRows.length;

  const clearSelection = () => {
    setSelectedRowKeys(new Set());
    setBulkRejectReason("");
  };

  const selectedRows = visibleCompOffData.filter((r) => isSelected(r));

  const buildBulkPayload = (row, status, reason) => ({
    id: row.id,
    user_id: row.user_id,
    attendance_type: "Comp off",
    reason: row.reason,
    status,
    earned_date: toISODate(row.earned_date),
    requested_date: toISODate(row.requested_date),
    ...(status === "Rejected" && { rejected_reason: reason || "" }),
  });

  // Sends a raw JSON array body — customAPI spreads its payload argument
  // ({...payload}), which would turn an array into a numeric-keyed object,
  // so bulk update goes straight to fetch instead (same reason the old
  // combined WorkAdjustment screen's bulk action bar does this).
  const callBulkAPI = async (status, reason) => {
    const payload = selectedRows.map((row) =>
      buildBulkPayload(row, status, reason),
    );
    try {
      const response = await fetch(
        `${domain}team_zone/approve/work_adjustment/actual-special-attendance/?company_id=${topbarCompanyId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );
      const res = await response.json();
      if (response.ok) {
        return { ok: true, code: response.status, responseDetails: res };
      }
      return {
        ok: false,
        code: response.status,
        message:
          res?.error ||
          res?.errors?.[0]?.message ||
          res?.message ||
          "Bulk update failed",
      };
    } catch (error) {
      return { ok: false, message: error.message };
    }
  };

  const handleBulkApprove = async () => {
    if (!selectedRows.length) return;
    setBulkLoading(true);
    const response = await callBulkAPI("Approved");
    if (response.ok) {
      showToast("success", "Records approved.");
      clearSelection();
      setReCallApi(true);
    } else {
      showToast("error", response?.message || "Failed to approve");
    }
    setBulkLoading(false);
  };

  const handleBulkReject = async () => {
    if (!selectedRows.length) return;
    setBulkLoading(true);
    const response = await callBulkAPI("Rejected", bulkRejectReason);
    if (response.ok) {
      showToast("success", "Records rejected.");
      clearSelection();
      setShowBulkRejectDialog(false);
      setReCallApi(true);
    } else {
      showToast("error", response?.message || "Failed to reject");
    }
    setBulkLoading(false);
  };

  const handleCompOffDateRange = (rangeStart, rangeEnd, rangeLabel) => {
    handleStartDate(rangeStart, rangeEnd);
    setDateRangeLabel(resolveRangeLabel(rangeLabel));
  };

  const handleCreateClosePanel = () => {
    setIsCreatePanelOpen(false);
  };

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const compOffColumnHeaders = useMemo(
    () => TeamZoneCompOff.filter((header) => header.key !== "expand"),
    [],
  );

  // Default View / saved-view selector — same shape as Leave/index.js. Its
  // own storageKey namespaces this screen's saved views separately from
  // WorkAdjustment's, even though both share the same subModuleId.
  const {
    savedViewsForScope,
    activeViewId,
    selectView,
    selectDefaultView,
    deleteView,
    createView,
    upsertView,
    updateView,
  } = useSavedHeaderViews({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "team_compoff_saved_views",
    scope: "default",
    onViewChange: handleSavedViewChange,
    defaultViewId: COMPOFF_LIST_DEFAULT_VIEW_ID,
  });
  const persistedDefaultViewId = activeViewId || COMPOFF_LIST_DEFAULT_VIEW_ID;
  const [currentViewId, setCurrentViewId] = useState(null);

  useEffect(() => {
    setCurrentViewId((prev) => {
      if (prev && prev !== COMPOFF_LIST_DEFAULT_VIEW_ID) {
        return prev;
      }
      return persistedDefaultViewId;
    });
  }, [persistedDefaultViewId]);

  const baseDefaultHeaders = useMemo(
    () => compOffColumnHeaders,
    [compOffColumnHeaders],
  );

  const storedDefaultView = useMemo(
    () =>
      savedViewsForScope.find(
        (view) => view.id === COMPOFF_LIST_DEFAULT_VIEW_ID,
      ) || null,
    [savedViewsForScope],
  );

  const customSavedViews = useMemo(
    () =>
      savedViewsForScope.filter(
        (view) => view.id !== COMPOFF_LIST_DEFAULT_VIEW_ID,
      ),
    [savedViewsForScope],
  );

  const defaultView = useMemo(() => {
    if (storedDefaultView?.isCustomDefault) {
      // Whatever headerConfig the user saved is authoritative — same as any
      // other saved view. (Previously this back-filled any header missing
      // from headerConfig, meant to surface columns added to the schema
      // after the view was saved — but that's indistinguishable from a
      // column the user deliberately unchecked, so it silently re-added
      // every unchecked header right back on the next render.)
      return storedDefaultView;
    }

    return {
      id: COMPOFF_LIST_DEFAULT_VIEW_ID,
      name: "Default View",
      headerConfig: baseDefaultHeaders.map((header) => ({
        key: header.key,
        name: header.name,
      })),
      headerKeys: baseDefaultHeaders.map((header) => header.key),
      isDefaultView: true,
    };
  }, [baseDefaultHeaders, storedDefaultView]);

  const effectiveViewId = currentViewId ?? persistedDefaultViewId;

  const activeView = useMemo(() => {
    if (effectiveViewId === COMPOFF_LIST_DEFAULT_VIEW_ID) {
      return defaultView;
    }

    return (
      customSavedViews.find((view) => view.id === effectiveViewId) ||
      defaultView
    );
  }, [customSavedViews, defaultView, effectiveViewId]);

  const visibleHeaders = useMemo(() => {
    const headerByKey = new Map(
      compOffColumnHeaders.map((header) => [header.key, header]),
    );

    if (activeView?.headerConfig?.length) {
      const orderedHeaders = activeView.headerConfig
        .map((entry) => {
          const baseHeader = headerByKey.get(entry.key);
          if (!baseHeader) return null;
          return {
            ...baseHeader,
            name: entry.name?.trim() || baseHeader.name,
          };
        })
        .filter(Boolean);

      return orderedHeaders.length ? orderedHeaders : baseDefaultHeaders;
    }

    if (activeView?.headerKeys?.length) {
      const selectedKeys = new Set(activeView.headerKeys);
      const filteredHeaders = compOffColumnHeaders.filter((header) =>
        selectedKeys.has(header.key),
      );
      return filteredHeaders.length ? filteredHeaders : baseDefaultHeaders;
    }

    return baseDefaultHeaders;
  }, [activeView, baseDefaultHeaders, compOffColumnHeaders]);

  const handleSelectView = useCallback(
    (viewId) => {
      setCurrentViewId(viewId);
      handleSavedViewChange();
    },
    [handleSavedViewChange],
  );

  const handleSelectDefaultView = useCallback(() => {
    setCurrentViewId(COMPOFF_LIST_DEFAULT_VIEW_ID);
    handleSavedViewChange();
  }, [handleSavedViewChange]);

  const handleMakeDefaultView = useCallback(
    (viewId) => {
      if (viewId === COMPOFF_LIST_DEFAULT_VIEW_ID) {
        selectDefaultView();
        setCurrentViewId(COMPOFF_LIST_DEFAULT_VIEW_ID);
        return;
      }

      selectView(viewId);
      setCurrentViewId(viewId);
    },
    [selectDefaultView, selectView],
  );

  const handleCreateView = useCallback(
    (viewName, headerConfig) => {
      createView(viewName, headerConfig, { activate: false });
    },
    [createView],
  );

  const handleDeleteView = useCallback(
    (viewId) => {
      if (currentViewId === viewId) {
        setCurrentViewId(COMPOFF_LIST_DEFAULT_VIEW_ID);
      }
      deleteView(viewId);
    },
    [currentViewId, deleteView],
  );

  const handleEditDefaultView = useCallback(
    (viewId, viewName, headerConfig) => {
      upsertView(
        viewId || COMPOFF_LIST_DEFAULT_VIEW_ID,
        viewName || "Default View",
        headerConfig,
        "Default view updated successfully.",
        { isCustomDefault: true },
      );
      setCurrentViewId(COMPOFF_LIST_DEFAULT_VIEW_ID);
    },
    [upsertView],
  );

  const titleOverride = useMemo(
    () => (
      <SavedViewSelector
        headers={compOffColumnHeaders}
        defaultHeaders={baseDefaultHeaders}
        defaultView={defaultView}
        savedViews={customSavedViews}
        activeView={activeView}
        activeViewId={effectiveViewId}
        persistedDefaultViewId={persistedDefaultViewId}
        screenAccess={check_access}
        onSelectDefaultView={handleSelectDefaultView}
        onSelectView={handleSelectView}
        onCreateView={handleCreateView}
        onUpdateView={(viewId, viewName, headerConfig) => {
          if (viewId === COMPOFF_LIST_DEFAULT_VIEW_ID) {
            handleEditDefaultView(viewId, viewName, headerConfig);
            return;
          }
          updateView(viewId, viewName, headerConfig);
        }}
        onDeleteView={handleDeleteView}
        onMakeDefaultView={handleMakeDefaultView}
      />
    ),
    [
      activeView,
      baseDefaultHeaders,
      check_access,
      customSavedViews,
      defaultView,
      effectiveViewId,
      handleCreateView,
      handleDeleteView,
      handleEditDefaultView,
      handleMakeDefaultView,
      handleSelectDefaultView,
      handleSelectView,
      compOffColumnHeaders,
      persistedDefaultViewId,
      updateView,
    ],
  );

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Date Range": `${moment(startDate).format("DD/MM/YYYY")} to ${moment(endDate).format("DD/MM/YYYY")}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [endDate, startDate, topbarCompanyName],
  );

  const newCompOffButton = (
    <NewRequestButton
      hasAccess={check_access}
      deniedMessage="Access denied: You are not authorized to create a comp off request"
      onClick={() => setIsCreatePanelOpen(true)}
    />
  );

  return loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box sx={{ flexGrow: 1, width: "100%", overflow: "visible" }}>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Mui.Typography sx={designType.pageTitle}>Comp Off</Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              Review and manage Comp Off requests that are awaiting approval.
            </Mui.Typography>
          </Box>

          <Mui.Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <ApprovalDateRangeFilter
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              onChange={handleCompOffDateRange}
              includeCurrentDate
              showToday
            />
            {newCompOffButton}
          </Mui.Stack>
        </Box>

        <CompOffStatusCards
          counts={compOffStatusCounts}
          activeFilter={statusCardFilter}
          onCardClick={handleStatusCardClick}
        />

        <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
          <CustomFilter
            title={"Comp Off Requests"}
            titleOverride={titleOverride}
            filterHeaders={visibleHeaders}
            data={compOffData}
            filteredData={visibleCompOffData}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            hideCount={true}
            downloadFormats={["excel", "pdf", "csv"]}
            includeMetadata={true}
            metadata={reportMetadata}
             labeledToolbar={true}
          />
        </Box>
        <BulkApprovalBar
          selectedCount={statusCardFilter === "Pending" ? selectedRowKeys.size : 0}
          loading={bulkLoading}
          onReject={() => setShowBulkRejectDialog(true)}
          onApprove={handleBulkApprove}
        />
        <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
          <Mui.TableContainer
            ref={containerRef}
            sx={{
              width: "100%",
              overflowX: { xs: "auto", sm: "visible" },
              overflowY: { xs: "auto", sm: "visible" },
              pb: footerHeight ? `${footerHeight}px` : 0,
            }}
          >
            <ApproveTable
              stickyHeader
              sx={{
                minWidth: 920,
                width: "100%",
                tableLayout: "fixed",
              }}
            >
              <Mui.TableHead sx={designShell.tableStickyHead}>
                <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                  {statusCardFilter === "Pending" && (
                    <Mui.TableCell
                      padding="checkbox"
                      sx={{
                        ...approveTableHeadCell,
                        top: APP_BAR_OFFSET + customFilterHeight,
                      }}
                    >
                      {statusCardFilter === "Pending" && pendingRows.length > 0 && (
                        <Mui.Checkbox
                          size="small"
                          sx={bulkSelectionCheckboxSx}
                          checked={isAllSelected}
                          indeterminate={isIndeterminate}
                          onChange={toggleAll}
                        />
                      )}
                    </Mui.TableCell>
                  )}
                  <Mui.TableCell
                    sx={{
                      ...approveTableHeadCell,
                      top: APP_BAR_OFFSET + customFilterHeight,
                      width: 40,
                    }}
                  />
                  {visibleHeaders?.map((value) => (
                    <Mui.TableCell
                      key={value.key}
                      sx={{
                        ...approveTableHeadCell,
                        top: APP_BAR_OFFSET + customFilterHeight,
                      }}
                    >
                      <TruncatedTableValue value={value?.name} />
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {visibleCompOffData?.length === 0 ? (
                  <TableEmptyState
                    dataLength={compOffData}
                    colSpan={visibleHeaders.length + 1 + (statusCardFilter === "Pending" ? 1 : 0)}
                  />
                ) : (
                  visibleCompOffData
                    ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    ?.map((value, index) => (
                      <React.Fragment key={value?.id}>
                        <Mui.TableRow
                          onClick={() => {
                            setSelectedData(value);
                            setViewCompOffDetails(true);
                          }}
                          sx={{
                            cursor: "pointer",
                            ...designShell.tableRowHover,
                            ...(viewCompOffDetails &&
                            value?.id === selectedData?.id
                              ? designShell.neutralSelectedRow
                              : {}),
                            "&:last-child td": { borderBottom: "none" },
                          }}
                        >
                          {statusCardFilter === "Pending" && (
                            <Mui.TableCell
                              padding="checkbox"
                              sx={approveTableBodyCell}
                              onClick={(event) => event.stopPropagation()}
                            >
                              {statusCardFilter === "Pending" && value.status === "Pending" && (
                                <Mui.Checkbox
                                  size="small"
                                  sx={bulkSelectionCheckboxSx}
                                  checked={isSelected(value)}
                                  onChange={() => toggleRow(value)}
                                />
                              )}
                            </Mui.TableCell>
                          )}
                          <Mui.TableCell
                            sx={approveTableBodyCell}
                            onClick={(event) => {
                              event.stopPropagation();
                              setExpandedIndex((prev) =>
                                prev === index ? null : index,
                              );
                              setExpandedData(value);
                            }}
                          >
                            <ArrowRightIcon
                              sx={{
                                color: designColors.slate500,
                                cursor: "pointer",
                                transition: "transform 0.2s ease",
                                transform:
                                  expandedIndex === index
                                    ? "rotate(90deg)"
                                    : "rotate(0deg)",
                              }}
                            />
                          </Mui.TableCell>
                          {visibleHeaders.map((head) => (
                            <Mui.TableCell key={head.key} data-label={head.name} sx={approveTableBodyCell}>
                              {head.key === "employee_name" ? (
                                <Mui.Stack direction="row" spacing={1} alignItems="center">
                                  <Mui.Avatar
                                    sx={{ width: 28, height: 28 }}
                                    alt={value?.employee_name?.charAt(0).toUpperCase()}
                                    src={value?.image_url}
                                  />
                                  <Mui.Typography
                                    sx={{
                                      fontWeight:
                                        viewCompOffDetails &&
                                        value?.id === selectedData?.id
                                          ? 700
                                          : 500,
                                      color:
                                        viewCompOffDetails &&
                                        value?.id === selectedData?.id
                                          ? designColors.slate900
                                          : "inherit",
                                      fontSize: 14,
                                    }}
                                  >
                                    {value?.employee_name || "N/A"}
                                  </Mui.Typography>
                                </Mui.Stack>
                              ) : head.key === "status" ? (
                                <Mui.Box sx={displayFlexItem}>
                                  <StatusBadge
                                    color={
                                      value?.color_code ||
                                      compOffStatusVisual(value?.status).accent
                                    }
                                    label={value?.status || "N/A"}
                                  />
                                </Mui.Box>
                              ) : (
                                (<TruncatedTableValue value={value[head.key] ?? "N/A"} />)
                              )}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>

                        {expandedIndex === index && (
                          <Mui.TableRow>
                            <Mui.TableCell
                              colSpan={visibleHeaders.length + 1 + (statusCardFilter === "Pending" ? 1 : 0)}
                              sx={{
                                p: 2,
                                borderBottom: `1px solid ${designColors.slate200}`,
                              }}
                            >
                              {getStatusFlow(expandedData)[expandedData?.status]?.map(
                                (item, i, arr) => (
                                  <StatusRow
                                    key={i}
                                    status={item.status}
                                    user={item.user}
                                    date={item.date}
                                    showConnector={i !== arr.length - 1}
                                  />
                                ),
                              )}
                              <ApprovalAuditTrail record={expandedData} compact />
                            </Mui.TableCell>
                          </Mui.TableRow>
                        )}
                      </React.Fragment>
                    ))
                )}
              </Mui.TableBody>
            </ApproveTable>
          </Mui.TableContainer>
          <Box ref={footerRef} sx={approveStickyFooterBar}>
            {paginationFooter({
              page,
              rowsPerPage,
              total: visibleCompOffData?.length || 0,
              itemLabel: "comp off requests",
              onPageChange: setPage,
              onRowsPerPageChange: handleChangeRowsPerPage,
              rowsPerPageOptions,
            })}
          </Box>
        </Box>
      </Box>
      <Apply
        open={isCreatePanelOpen}
        onClose={() => {
          handleCreateClosePanel();
        }}
        setReCallApi={setReCallApi}
        expanded={createCompOffPanelExpanded}
        onToggleExpand={toggleCreateCompOffPanelExpanded}
      />
      <CompOffDetailPanel
        open={viewCompOffDetails}
        onClose={() => setViewCompOffDetails(false)}
        compOff={selectedData}
        checkAccess={check_access}
        setReCallApi={setReCallApi}
        expanded={compOffPanelExpanded}
        onToggleExpand={toggleCompOffPanelExpanded}
      />
      <GlobalConfirmDialog
        open={showBulkRejectDialog}
        title="Reject Selected Requests"
        onCancel={() => {
          setShowBulkRejectDialog(false);
          setBulkRejectReason("");
        }}
        onConfirm={handleBulkReject}
        confirmLabel="Reject"
        cancelLabel="Cancel"
        loading={bulkLoading}
        danger
        reasonValue={bulkRejectReason}
        onReasonChange={setBulkRejectReason}
        reasonPlaceholder="Enter a reason for rejection"
        reasonMinRows={2}
        maxWidth="sm"
      >
        <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
          Reason (optional) - applied to all selected Comp Off requests.
        </Mui.Typography>
      </GlobalConfirmDialog>
    </React.Fragment>
  );
};
